import React, { Component } from 'react';
import isEmpty from 'lodash.isempty';
import loadable from '@loadable/component';

import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { Redirect, withRouter } from 'react-router-dom';

// constants
import { WEB_URL } from '/src/utilities/endpoints';

// helpers
import { getItemWebUrl } from '/src/utilities/helpers';
import { PUBLISHING_STATUS, HUBSPOT_FORM_SOURCE, SALES_CONTACT_REASONS_VALUE, REPORT_TYPES } from '/src/utilities/constants';

// reusable components
import MagnittBanner from '/src/micro-frontends/MagnittBanner';
import ContactSalesPopup from '/src/micro-frontends/ContactSalesPopup';
import ContentImage from '/src/reusable-components/ContentImg';
import SponsorFocus from './SponsorFocus';
import { ResearchActions } from '/src/widgets/ResearchActions';
import ResearchDownloadForm from '/src/reusable-components/ResearchDownloadForm';
import MessagePopup from '/src/reusable-components/MessagePopup';
import TableOfContents from '/src/reusable-components/TableOfContents';
import TextFooter from '/src/reusable-components/TextFooter';
import RelatedArticles from '/src/widgets/RelatedArticles';
import { modifyElementsForSEO } from '../../utilities/helpers';

const ContentBody = loadable(() =>
    import(/* webpackChunkName: "ContentBody" */ '/src/reusable-components/ContentBody'),
);
const ContentTag = loadable(() =>
    import(/* webpackChunkName: "ContentTag" */ '/src/reusable-components/ContentTag'),
);



class SingleResearch extends Component {

    constructor(props) {
        super(props);

        let data;
        if (__isBrowser__) {
            data = window.__INITIAL_DATA__.data;
        } else {
            data = props.staticContext.data;
        }

        let tempData = data.researchData;

        this.state = {
            data: tempData ? tempData : null,
            objectUrl: tempData ? getItemWebUrl('research', tempData.id, tempData.code) : null,
            downloadError: '',
            modalTitle: '',
            isErrorModal: false
        };
    }

    componentDidMount() {
        const { data } = this.state;
        if (data) {
            this.getInitialData();
        }
    }

    getInitialData = () => {
        let object = this.state.data;
        let { metadesc } = '';
        let image_data = object.image_data;
        object.image_data.path = !isEmpty(image_data) ? encodeURI(image_data.path) : undefined;

        if (object.body !== undefined && object.body !== null) {

            // replace all occurrences of <a> tags without href attributes
            const docBodyInnerHTML = modifyElementsForSEO(object);

            // Serialize the modified HTML back to a string
            object.body = docBodyInnerHTML;

            // Generate meta description
            metadesc = object.body.replace(/<\/?[^>]+(>|$)/g, '');
            metadesc = metadesc.replace(/&nbsp;/g, ' ');
            metadesc = metadesc.replace(/&nbsp;/g, ' ');
            if (metadesc !== undefined && metadesc !== null && metadesc.length > 300) {
                metadesc = metadesc.substr(0, 300);
                metadesc = metadesc.substr(0, metadesc.lastIndexOf(' ')) + ' ...';
            }
        }
        object.metadesc = metadesc;

        if (object.published_at) {
            let date = new Date(object.published_at);
            let options = { year: 'numeric', month: 'long' };
            object.time = date.toLocaleString('en-US', options);
        }
        this.setState({ data: object });
    };

    render() {

        const object = this.state.data;

        // research id not found / invalid research id
        if (object === 404 || isEmpty(object) || object.id === null) {
            return <Redirect to="/not-found" />;
        }

        const { image_data, metadesc, title, time, is_published, short_description, name } = this.state.data;
        const { objectUrl } = this.state;

        let displayPrice = "Report";
        if (object.type_id == REPORT_TYPES.paid) {
            displayPrice = "Premium Report";
        } else if (object.type_id === REPORT_TYPES.free) {
            displayPrice = "Free Report"
        } else if (object.type_id === REPORT_TYPES.sponsored) {
            displayPrice = "Sponsored Report";
        }

        return (
            <div className="footer-margin-offset">
                <Helmet
                    title={`${name} | MAGNiTT`}
                    link={[
                        {
                            rel: 'canonical',
                            href: `${WEB_URL}/${objectUrl}`,
                        },
                        {
                            rel: "preload",
                            as: "image",
                            href: object?.image_data?.path,
                            fetchPriority: 'high'
                        }
                    ]}
                    meta={[
                        {
                            name: "twitter:card",
                            content: "summary_large_image"
                        },
                        {
                            name: "twitter:site",
                            content: "@MAGNITT"
                        },
                        {
                            name: "twitter:creator",
                            content: "@MAGNITT"
                        },
                        {
                            name: "description",
                            content: short_description
                        },
                        {
                            name: "twitter:title",
                            content: name
                        },
                        {
                            name: "twitter:description",
                            content: short_description
                        },
                        {
                            name: "twitter:image",
                            content: object ? image_data.path : ''
                        },
                        {
                            name: "twitter:image:alt",
                            content: name
                        },
                        {
                            property: 'og:description',
                            content: short_description,
                        },
                        {
                            name: 'abstract',
                            content: short_description,
                        },
                        {
                            property: 'og:type',
                            content: 'article',
                        },
                        {
                            property: 'og:url',
                            content: `${WEB_URL}/${objectUrl}`,
                        },
                        {
                            property: 'og:title',
                            content: name,
                        },
                        {
                            property: 'og:image',
                            content: object ? image_data.path : '',
                        },
                        {
                            property: 'fb:app_id',
                            content: '206675253001485',
                        },
                        {
                            property: 'og:image:secure_url',
                            content: object ? image_data.path : '',
                        }
                    ]}
                    script={[
                        {
                            type: 'application/ld+json',
                            innerHTML: JSON.stringify({
                                "@context": "https://schema.org",
                                "@type": "BreadcrumbList",
                                "itemListElement": [{
                                    "@type": "ListItem",
                                    "position": 1,
                                    "name": "research",
                                    "item": `${WEB_URL}/research`  // Template literal outside quotes
                                },
                                {
                                    "@type": "ListItem",
                                    "position": 2,
                                    "name": name,  // Direct variable reference
                                    "item": `${WEB_URL}/${objectUrl}`  // Template literal outside quotes
                                }]
                            })
                        }
                    ]}
                />

                <section className="directory content-box">
                    <div className="content-block">
                        <div className="row flex-grow-1">
                            <div className="column-full">
                                {/* <MagnittBanner data={{ match: this.props.match }} /> */}

                                {object && (
                                    <div className="mid-width-info-box">
                                        <div className="info-box-container">
                                            <div className="info-box-body content-body">

                                                <div lang={object.lang}>

                                                    <ContentImage
                                                        data={object}
                                                        socialTitle={name}
                                                        type="research"
                                                    />

                                                    <div className="page-header">

                                                        <div className="row mt-0">
                                                            <div className="col-lg-9">

                                                                {/* title */}
                                                                <div className="article-title">
                                                                    <h1>{name}</h1>
                                                                    {object.subtitle &&
																		<h2>{object.subtitle}</h2>
                                                                    }
                                                                </div>

                                                                <div className="article-info-line">
                                                                    <div className="article-info-with-divider">
                                                                        <span className="article-info-report-type">
                                                                            {displayPrice}
                                                                        </span>

                                                                        {/* posted time */}
                                                                        <span className="article-info-date">
                                                                            {time
                                                                                ? `${time}`
                                                                                : (is_published === PUBLISHING_STATUS.unpublished)
                                                                                    ? 'Unpublished'
                                                                                    : (is_published === PUBLISHING_STATUS.draft &&
																						'Draft') || ''
                                                                            }
                                                                        </span>

                                                                        <ContentTag
                                                                            tagdata={object}
                                                                            type={'research'}
                                                                            displayMax={2}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {!isEmpty(object.sponsor_logo_data) && (
                                                                    <div className="article-sponsor-line">
                                                                        Sponsored by
                                                                        {object.preview_3 && (
                                                                            <> {object.preview_3}</>
                                                                        )}
                                                                        <img
                                                                            className="sponsored-logo"
                                                                            src={object.sponsor_logo_data.path}
                                                                            alt={`Sponsored-logo`}
                                                                        />
                                                                    </div>
                                                                )}

                                                            </div>

                                                            <div className="col-lg-3 mt-0 align-self-end">
                                                                <div className="article-action-container">
                                                                    <ResearchActions
                                                                        data={this.state.data}
                                                                        user={this.props.user}
                                                                        setParentState={(s, c) => { this.setState(s, c) }}
                                                                    />
                                                                </div>
                                                            </div>

                                                        </div>

                                                    </div>

                                                    <hr />

                                                    <div className="row">
                                                        <div className="content-column-side">

                                                            <TableOfContents report={object} />

                                                            {/*
                                                              * hide these widgets on mobile
                                                              * as mobile version is integrated in the body content
                                                              */}
                                                            <div className="hidden-lg-down mt-0">

                                                                {/* custom widgets from admin panel */}
                                                                {/* the widgets are added through the ReactDOM.render function in ContentBody.js */}
                                                                <div id="custom-widgets-container"></div>

                                                                {/*
                                                                  * sponsor focus
                                                                  * not displayed on mobile/tablet to simplify the layout
                                                                  */}
                                                                {object.preview_2 && !isEmpty(object.sponsor_logo_data) && (
                                                                    <SponsorFocus
                                                                        img={object.sponsor_logo_data.path}
                                                                        imgAlt={'sponsored-logo'}
                                                                        text={object.preview_2}
                                                                    />
                                                                )}

                                                            </div>

                                                        </div>
                                                        <div className="content-column-main">
                                                            <ContentBody
                                                                data={this.state.data}
                                                                history={this.props.history}
                                                                type="research"
                                                                user={this.props.user}
                                                            />
                                                        </div>
                                                    </div>

                                                </div>

                                                <TextFooter
                                                    image={object.image_data.path}
                                                    description={metadesc}
                                                    title={object.name}
                                                    url={`${WEB_URL}/${objectUrl}`}
                                                    data={this.state.data}
                                                />

                                                <RelatedArticles
                                                    displayCount={3}
                                                    itemId={this.state.data.id}
                                                    type="research"
                                                />

                                            </div>
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </section>

                {/* contact sales popup */}
                <ContactSalesPopup data={{ page: HUBSPOT_FORM_SOURCE.research_purchase_btn, preSelectedReason: SALES_CONTACT_REASONS_VALUE.accessSpecificReports }} />

                {/* download form modal */}
                {/* used for free reports when user is logged out */}
                <ResearchDownloadForm data={this.state.data} user={this.props.user} />

                {/* message for report download errors or no quota left */}
                <MessagePopup
                    title={this.state.modalTitle}
                    data={this.state.downloadError}
                    isError={this.state.isErrorModal}
                />

            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.userReducer,
});

export default withRouter(connect(mapStateToProps, {})(SingleResearch));
